// Wealth → CFO: types and calls for /api/cfo/* (CEO Tasks' /api/v1/cfo/* contract, same bodies and responses)
import { api, del, patch, post } from "../../lib/api";
import { errText } from "./shared";

export interface CfoFile {
  id: string; name: string; origName: string; type: string; size: number; uploadedAt: string;
  status: "reading" | "proposed" | "filed"; folder: string;
  proposal: { folder?: string; name?: string; docType?: string; entity?: string; period?: string } | null;
  summary: string; textChars: number; method: string;
  drive: { link: string; at: string; error: string; current: boolean } | null;
  sessionId: string | null; duplicateOf?: string;
}
export interface CfoUploaded extends Partial<CfoFile> { error?: string }
export interface CfoFact {
  id: string; text: string; entity?: string; asOf?: string; status: "proposed" | "confirmed"; createdAt: string;
  source?: { type?: string; name?: string; where?: string; fileId?: string; sessionId?: string };
}
export interface CfoAction { type?: string; name: string; value: number; note?: string }
export interface CfoWeb {
  on: boolean; used?: boolean; questions?: string[]; unsourced?: string[]; errors?: string[];
  sources?: Array<{ url: string; title?: string; host?: string }>; pages?: Array<{ url: string; title?: string }>;
}
export interface CfoMessage {
  role: "user" | "assistant"; content: string; ts: string; files?: string[]; removedFiles?: string[];
  model?: string; fellBack?: boolean; deep?: boolean; calcs?: number; calcFailed?: number; unverified?: string[];
  usage?: { costUsd?: number | null } | null; web?: CfoWeb; factIds?: string[]; actions?: CfoAction[];
  applied?: Record<string, string>; sources?: { passages?: string[]; files?: string[] };
}
export interface CfoSession { id: string; title: string; createdAt: string; updatedAt: string; messages: CfoMessage[] }
export interface CfoSessionRow { id: string; title: string; createdAt: string; updatedAt: string; count: number }
export interface CfoDrive {
  connected: boolean; root: string; rootLink: string; account: string; mirrored: number; pending: number; lastSyncAt: string | null;
  needsSignIn: boolean; quotaFull: boolean; lastError: string; note: string;
}
export interface CfoUsage { month: string; rows: Array<{ model: string; calls: number; in: number; out: number; costUsd: number | null }>; costUsd: number; calls: number; note: string }
export interface CfoState {
  models: { chat: string; deep: string; filing: string; fallback: string; search: string };
  usage?: CfoUsage; encrypted: boolean; drive: CfoDrive; folders: string[];
  /** Filed documents go to FusionDocs (owner-only) instead of the Google Drive mirror. */
  fusionDocs?: { on: boolean; accessReady: boolean; filed: number; pending: number; lastError: string; lastSyncAt: string | null };
  sessions: CfoSessionRow[]; files: CfoFile[]; facts: CfoFact[];
}

export const CK = ["cfo", "state"] as const;
const B = "/api/cfo";

export const cfo = {
  state: () => api<CfoState>(`${B}/state`),
  newSession: (title?: string) => post<{ session: CfoSession }>(`${B}/sessions`, { title }),
  getSession: (id: string) => api<{ session: CfoSession }>(`${B}/sessions/${id}`),
  renameSession: (id: string, title: string) => patch<{ session: { id: string; title: string } }>(`${B}/sessions/${id}`, { title }),
  deleteSession: (id: string) => del(`${B}/sessions/${id}`),
  send: (id: string, text: string, files: string[], deep: boolean, web: boolean) =>
    post<{ message: CfoMessage; user: CfoMessage; title: string; facts: CfoFact[] }>(`${B}/sessions/${id}/messages`, { text, files, deep, web }),
  markApplied: (id: string, ts: string, idx: number, label: string) => post(`${B}/sessions/${id}/applied`, { ts, idx, label }),
  upload: (files: Array<{ name: string; type: string; contentBase64: string }>, sessionId?: string) => post<{ files: CfoUploaded[] }>(`${B}/upload`, { files, sessionId }),
  fileIt: (id: string, where: { folder?: string; name?: string }) => post(`${B}/files/${id}/file`, where),
  deleteFile: (id: string) => del(`${B}/files/${id}`),
  facts: (body: { confirm?: string[]; reject?: string[]; text?: string; entity?: string; asOf?: string }) => post<{ facts: CfoFact[] }>(`${B}/facts`, body),
  patchFact: (id: string, p: { text?: string; entity?: string; asOf?: string }) => patch(`${B}/facts/${id}`, p),
  deleteFact: (id: string) => del(`${B}/facts/${id}`),
  driveSync: () => post(`${B}/drive/sync`, {}),
  patchAsset: (ref: { name: string; value: number; notes?: string; method?: string }) =>
    patch<{ ok: boolean; asset: { name: string; value: number }; before: number }>("/api/wealth/asset", ref),
};
export const downloadUrl = (id: string) => `${B}/files/${id}/download`;

/** message ?? error; a reply that is not JSON (a proxy timeout page) gets a plain sentence. */
export const cfoErr = (e: unknown) => (e instanceof SyntaxError ? "CFO request failed (the server sent no readable answer)" : errText(e));

export const readFile = (file: File) =>
  new Promise<{ name: string; type: string; contentBase64: string }>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, type: file.type, contentBase64: String(r.result).split(",")[1] || "" });
    r.onerror = () => reject(new Error(`Could not read ${file.name}`));
    r.readAsDataURL(file);
  });

export const fmtSize = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
export const fmtOnDay = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
export const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
export const modelName = (m?: string) =>
  String(m || "").replace(/^openai:/, "OpenAI ").replace(/^anthropic:/, "Claude ").replace(/claude-/, "").replace(/-\d{8}$/, "");
